// Half-circle SVG gauge.
// - Normal mode: fills from `min` (left) up to `value`.
// - Bipolar mode: zero sits at the top-center; negative values fill an arc to
//   the LEFT of center, positive values fill to the RIGHT (proportionally).
export default function Gauge({
  value,
  min = 0,
  max = 100,
  unit = "",
  label,
  color = "#10B981",
  size = 180,
  testid,
  bipolar = false,
}) {
  const v = typeof value === "number" ? value : 0;
  const clamped = Math.max(min, Math.min(max, v));
  const pct = max === min ? 0 : (clamped - min) / (max - min);

  const stroke = 14;
  const r = (size - stroke) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circ = Math.PI * r; // half circumference

  // fraction 0 -> left (180deg), 1 -> right (0deg), 0.5 -> top center
  const pointAt = (fraction) => {
    const angle = Math.PI * (1 - fraction);
    return { x: cx + r * Math.cos(angle), y: cy - r * Math.sin(angle) };
  };
  const start = pointAt(0);
  const end = pointAt(1);

  // For bipolar: draw the coloured segment between the zero point and value.
  const zeroFrac = bipolar ? Math.max(0, Math.min(1, (0 - min) / (max - min))) : 0;
  const segA = Math.min(zeroFrac, pct);
  const segB = Math.max(zeroFrac, pct);
  const pa = pointAt(segA);
  const pb = pointAt(segB);
  const hasSeg = Math.abs(pct - zeroFrac) > 0.0005;
  const zeroPt = pointAt(zeroFrac);

  return (
    <div className="flex flex-col items-center" data-testid={testid}>
      <div className="relative" style={{ width: size, height: size / 2 + 12 }}>
        <svg width={size} height={size / 2 + 12}>
          {/* track */}
          <path
            d={`M ${start.x} ${start.y} A ${r} ${r} 0 0 1 ${end.x} ${end.y}`}
            fill="none"
            stroke="#E2E8F0"
            strokeWidth={stroke}
            strokeLinecap="round"
          />
          {bipolar ? (
            <>
              {hasSeg && (
                <path
                  d={`M ${pa.x} ${pa.y} A ${r} ${r} 0 0 1 ${pb.x} ${pb.y}`}
                  fill="none"
                  stroke={color}
                  strokeWidth={stroke}
                  strokeLinecap="round"
                />
              )}
              {/* zero tick at the center of the scale */}
              <circle cx={zeroPt.x} cy={zeroPt.y} r={3} fill="#94A3B8" />
            </>
          ) : (
            <path
              d={`M ${start.x} ${start.y} A ${r} ${r} 0 0 1 ${end.x} ${end.y}`}
              fill="none"
              stroke={color}
              strokeWidth={stroke}
              strokeLinecap="round"
              strokeDasharray={circ}
              strokeDashoffset={circ * (1 - pct)}
              style={{ transition: "stroke-dashoffset 0.6s ease" }}
            />
          )}
        </svg>
        <div className="absolute inset-x-0 bottom-0 flex flex-col items-center">
          <span className="font-mono text-2xl font-bold tracking-tight text-slate-900">
            {typeof value === "number" ? value : "—"}
          </span>
          <span className="text-[11px] font-medium text-slate-400">{unit}</span>
        </div>
      </div>
      <div className="mt-1 text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">
        {label}
      </div>
      <div className="flex w-full justify-between px-2 text-[10px] text-slate-400">
        <span>{min}</span>
        {bipolar && <span>0</span>}
        <span>{max}</span>
      </div>
    </div>
  );
}
